import { useEffect, useMemo, useState, type ReactElement } from "react";
import type { AvatarConfig, HatId, PatternId } from "@shared/types";
import { api } from "../api";
import { UNLOCKS, type UnlockItem } from "../local/unlocks";
import { AvatarViewer3D } from "./AvatarViewer3D";
import { hatThumbnail, colorThumbnail, patternThumbnail } from "./itemThumbnails";

interface Props {
  initial: AvatarConfig;
  onSaved?: (next: AvatarConfig) => void;
  /** When true, AvatarEditor is purely controlled — no Save button or API call.
   *  Used by the "create player" flow where the parent submits everything at once.
   */
  controlledOnly?: boolean;
  onChange?: (cfg: AvatarConfig) => void;
  localOnly?: boolean;
  /** Unlock ids the player has earned. Locked items render disabled. */
  unlocked?: string[];
  /** Current lifetime-runs count, used to show "Unlocks at N" hints. */
  lifetimeRuns?: number;
}

const DEFAULTS: Required<Pick<AvatarConfig, "skinColor" | "shirtColor" | "pantsColor">> = {
  skinColor: "#d7a47f",
  shirtColor: "#ff2ca4",
  pantsColor: "#334155",
};

// Simplified to the three things kids actually change: hat, body colour,
// and pattern. (Outfit / face / pants / skin were removed to declutter.)
type Tab = "hat" | "color" | "pattern";
const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "hat",     label: "Hat",     icon: "👑" },
  { id: "color",   label: "Color",   icon: "🎨" },
  { id: "pattern", label: "Pattern", icon: "✦" },
];

export function AvatarEditor({ initial, onSaved, controlledOnly, onChange, localOnly, unlocked, lifetimeRuns = 0 }: Props) {
  const [cfg, setCfg] = useState<AvatarConfig>({ ...DEFAULTS, ...initial });
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<Tab>("hat");

  useEffect(() => {
    setCfg({ ...DEFAULTS, ...initial });
  }, [initial]);

  const unlockSet = useMemo(
    () => new Set(unlocked ?? UNLOCKS.filter((u) => u.unlockAt === 0).map((u) => u.id)),
    [unlocked],
  );
  const hatItems = useMemo(() => UNLOCKS.filter((u) => u.kind === "hat"), []);
  const patternItems = useMemo(() => UNLOCKS.filter((u) => u.kind === "pattern"), []);
  const shirtColorItems = useMemo(() => UNLOCKS.filter((u) => u.kind === "shirt-color"), []);

  const set = (k: keyof AvatarConfig, v: string) => {
    setCfg((c) => {
      const next = { ...c, [k]: v };
      onChange?.(next);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      if (!localOnly) await api.patch("/api/students/me", { avatarConfig: cfg });
      setSavedAt(Date.now());
      onSaved?.(cfg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={controlledOnly ? "avatar-lab" : "avatar-lab avatar-lab-card"}>
      <div className="avatar-lab-stage">
        <AvatarViewer3D avatar={cfg} />
      </div>

      <div className="avatar-lab-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`avatar-lab-tab ${tab === t.id ? "is-active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            <span className="avatar-lab-tab-icon">{t.icon}</span>
            <span className="avatar-lab-tab-label">{t.label}</span>
          </button>
        ))}
      </div>

      <div className="avatar-lab-trayhead">
        <strong>{TABS.find((t) => t.id === tab)?.label}</strong>
        {(() => {
          const items = tab === "hat" ? hatItems : tab === "pattern" ? patternItems : shirtColorItems;
          const idx = items.findIndex((it) =>
            tab === "hat" ? (cfg.hatId ?? "none") === (it.apply.hatId ?? "none")
            : tab === "pattern" ? (cfg.patternId ?? "solid") === (it.apply.patternId ?? "solid")
            : cfg.shirtColor === it.apply.shirtColor);
          return <span className="avatar-lab-traycount">{Math.max(1, idx + 1)} / {items.length}</span>;
        })()}
      </div>

      <div className="avatar-lab-tray">
        {tab === "hat" && (
          <UnlockGrid
            items={hatItems}
            unlockSet={unlockSet}
            lifetimeRuns={lifetimeRuns}
            isSelected={(item) => (cfg.hatId ?? "none") === (item.apply.hatId ?? "none")}
            renderThumb={(item) => <HatThumb id={item.apply.hatId ?? "none"} />}
            onPick={(item) => set("hatId" as keyof AvatarConfig, (item.apply.hatId ?? "none") as string)}
          />
        )}
        {tab === "pattern" && (
          <UnlockGrid
            items={patternItems}
            unlockSet={unlockSet}
            lifetimeRuns={lifetimeRuns}
            isSelected={(item) => (cfg.patternId ?? "solid") === (item.apply.patternId ?? "solid")}
            renderThumb={(item) => <PatternThumb id={item.apply.patternId ?? "solid"} color={cfg.shirtColor ?? DEFAULTS.shirtColor} />}
            onPick={(item) => set("patternId" as keyof AvatarConfig, (item.apply.patternId ?? "solid") as string)}
          />
        )}
        {tab === "color" && (
          <UnlockGrid
            items={shirtColorItems}
            unlockSet={unlockSet}
            lifetimeRuns={lifetimeRuns}
            isSelected={(item) => cfg.shirtColor === item.apply.shirtColor}
            renderThumb={(item) => <ColorThumb color={item.apply.shirtColor!} />}
            onPick={(item) => set("shirtColor", item.apply.shirtColor!)}
          />
        )}
      </div>

      {!controlledOnly && (
        <div className="avatar-lab-footer">
          <span className="avatar-lab-saved">
            {savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString()}` : ""}
          </span>
          <button className="btn-primary" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save look"}
          </button>
        </div>
      )}
    </div>
  );
}

function UnlockGrid({
  items,
  unlockSet,
  lifetimeRuns,
  isSelected,
  renderThumb,
  onPick,
}: {
  items: UnlockItem[];
  unlockSet: Set<string>;
  lifetimeRuns: number;
  isSelected: (item: UnlockItem) => boolean;
  renderThumb: (item: UnlockItem) => ReactElement;
  onPick: (item: UnlockItem) => void;
}) {
  return (
    <div className="avatar-lab-grid">
      {items.map((item) => {
        const isUnlocked = unlockSet.has(item.id);
        const selected = isSelected(item);
        const remaining = Math.max(0, item.unlockAt - lifetimeRuns);
        return (
          <button
            key={item.id}
            className={`avatar-lab-cell ${selected ? "is-selected" : ""} ${isUnlocked ? "" : "is-locked"}`}
            onClick={() => isUnlocked && onPick(item)}
            disabled={!isUnlocked}
            title={isUnlocked ? item.name : `Unlocks in ${remaining} run${remaining === 1 ? "" : "s"}`}
          >
            {selected && <span className="avatar-lab-cell-check" aria-hidden>✓</span>}
            <div className="avatar-lab-cell-thumb">
              {renderThumb(item)}
              {!isUnlocked && <span className="avatar-lab-cell-lock">🔒</span>}
            </div>
            <span className="avatar-lab-cell-name">{item.name}</span>
            {!isUnlocked && (
              <span className="avatar-lab-cell-hint">in {remaining}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function HatThumb({ id }: { id: string }) {
  if (id === "none") {
    return <div className="avatar-lab-thumb-none" aria-hidden>⊘</div>;
  }
  const url = hatThumbnail(id as HatId);
  if (!url) return <div className="avatar-lab-thumb-hat" />;
  return <img className="avatar-lab-thumb-img" src={url} alt="" draggable={false} />;
}

function PatternThumb({ id, color }: { id: string; color: string }) {
  const url = patternThumbnail(id as PatternId, color);
  if (!url) {
    const bg =
      id === "stripes" ? `repeating-linear-gradient(45deg, ${color} 0 10px, #ffffff 10px 20px)` :
      id === "dots" ? `radial-gradient(#ffffff 22%, transparent 26%) ${color}` :
      id === "star" ? `radial-gradient(#ffd166 22%, transparent 26%) ${color}` : color;
    return <div className="avatar-lab-thumb-pat" style={{ background: bg, backgroundSize: id === "dots" || id === "star" ? "22px 22px" : "auto" }} />;
  }
  return <img className="avatar-lab-thumb-img" src={url} alt="" draggable={false} />;
}

function ColorThumb({ color }: { color: string }) {
  const url = colorThumbnail(color);
  if (!url) return <div className="avatar-lab-thumb-color" style={{ background: color }} />;
  return <img className="avatar-lab-thumb-img" src={url} alt="" draggable={false} />;
}

/** Stylized blocky avatar preview. `large` doubles the size for the lab. */
export function AvatarPreview({ cfg, large }: { cfg: AvatarConfig; large?: boolean }) {
  const { skinColor = DEFAULTS.skinColor, shirtColor = DEFAULTS.shirtColor, pantsColor = DEFAULTS.pantsColor, hatId, patternId } = cfg;
  const torsoBg = patternBackground(patternId, shirtColor);
  return (
    <div className={`avatar-preview ${large ? "is-large" : ""}`}>
      {hatId && hatId !== "none" && <HatPreview id={hatId} />}
      <div className="ap-head" style={{ background: skinColor }}>
        <div className="ap-eye" style={{ left: "28%" }} />
        <div className="ap-eye" style={{ right: "28%" }} />
      </div>
      <div className="ap-shoulders">
        <div className="ap-arm" style={{ background: skinColor }} />
        <div className="ap-torso" style={torsoBg} />
        <div className="ap-arm" style={{ background: skinColor }} />
      </div>
      <div className="ap-legs">
        <div className="ap-leg" style={{ background: pantsColor }} />
        <div className="ap-leg" style={{ background: pantsColor }} />
      </div>
    </div>
  );
}

function patternBackground(patternId: string | undefined, color: string): React.CSSProperties {
  if (!patternId || patternId === "solid") return { background: color };
  if (patternId === "stripes") {
    return { background: `repeating-linear-gradient(45deg, ${color} 0 10px, #ffffff 10px 20px)` };
  }
  if (patternId === "dots") {
    return { background: `radial-gradient(#ffffff 22%, transparent 26%) ${color}`, backgroundSize: "14px 14px" };
  }
  if (patternId === "star") {
    return { background: `radial-gradient(#ffd166 22%, transparent 26%) ${color}`, backgroundSize: "14px 14px" };
  }
  return { background: color };
}

function HatPreview({ id }: { id: HatId }) {
  if (id === "cap-blue" || id === "cap-red") {
    const c = id === "cap-blue" ? "#2563eb" : "#dc2626";
    return (
      <div className="ap-hat-cap">
        <div className="ap-cap-crown" style={{ background: c }} />
        <div className="ap-cap-brim" style={{ background: c }} />
      </div>
    );
  }
  if (id === "beanie") {
    return (
      <div className="ap-hat-beanie">
        <div className="ap-beanie-body" />
        <div className="ap-beanie-pom" />
      </div>
    );
  }
  if (id === "crown") {
    return (
      <div className="ap-hat-crown">
        <span>♛</span>
      </div>
    );
  }
  if (id === "party-hat") {
    return (
      <div className="ap-hat-crown" style={{ background: "#a855f7" }}>
        <span style={{ color: "#ffd166" }}>🎉</span>
      </div>
    );
  }
  if (id === "halo") {
    return (
      <div className="ap-hat-crown" style={{ background: "transparent", color: "#fef08a", textShadow: "0 0 12px #fff3a8" }}>
        <span>◯</span>
      </div>
    );
  }
  return null;
}
